/** @jsx h */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { CloseIcon } from './yourListsIcons';

// Keep in sync with the CSS transition on .page--ld-confirm-overlay--fade.
const TRANSITION_MS = 250;

/** Replace {token} placeholders in an i18n string. */
const fill = (str, vars) => str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');

/**
 * Shows the outcome of an "add to cart" — how much of each item was added,
 * updated, unchanged, capped by stock, or skipped (out of stock).
 * `report` is what updateCart() returns ({ lines: [...] }), or null (hidden).
 * Fades in/out: stays mounted through the exit transition.
 */
function CartResultModal({ report, i18n = {}, onClose }) {
  const t = (key) => i18n[key] || key;
  const [rendered, setRendered] = useState(false);
  const [active, setActive] = useState(false);
  // Hold the last report so content stays during the exit transition.
  const [shown, setShown] = useState(report);

  useEffect(() => {
    if (report) {
      setShown(report);
      setRendered(true);
      const raf = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(raf);
    }
    setActive(false);
    const timer = setTimeout(() => setRendered(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [report]);

  if (!rendered) return null;
  const lines = shown?.lines || [];

  return (
    <div
      className={`page--ld-confirm-overlay page--ld-confirm-overlay--fade${
        active ? ' is-open' : ''
      }`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className="page--ld-confirm"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="page--ld-confirm-close"
          aria-label={t('cancel')}
          onClick={onClose}
        >
          <CloseIcon />
        </button>

        <div className="page--ld-confirm-title">{t('cart_updated_title')}</div>

        <ul className="page--ld-cart-result">
          {lines.map((l) => (
            <li
              key={l.id}
              className={`page--ld-cart-result-item${l.capped || l.oos ? ' is-capped' : ''}`}
            >
              {(() => {
                if (l.oos) return fill(t('cart_oos_skipped'), l);
                if (l.capped) return fill(t('cart_capped'), l);
                if (l.before === l.applied) return fill(t('cart_unchanged'), l);
                if (l.before > 0) return fill(t('cart_updated'), l);
                return fill(t('cart_added'), l);
              })()}
            </li>
          ))}
        </ul>

        <div className="page--ld-confirm-actions">
          <button
            type="button"
            className="page--ld-confirm-delete"
            onClick={onClose}
          >
            {t('ok')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CartResultModal;
